import React, { useState } from 'react';
import { addMissedFast, addBulkMissedFasts } from '../firebase/collections';
import { auth } from '../firebase';
import { CalendarIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const PREDEFINED_REASONS = [
  { value: 'travel', label: 'Travel', icon: '✈️' },
  { value: 'sickness', label: 'Sickness', icon: '🤒' },
  { value: 'menstruation', label: 'Menstruation', icon: '🌙' },
  { value: 'pregnancy', label: 'Pregnancy', icon: '👶' },
  { value: 'other', label: 'Other', icon: '📝' }
];

function AddFastForm({ onSuccess }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateRange, setIsDateRange] = useState(false);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!auth.currentUser) {
        throw new Error('You must be signed in to add a missed fast');
      }

      const fastData = {
        reason: reason === 'other' ? 'other' : reason,
        displayReason: reason === 'other' ? customReason : PREDEFINED_REASONS.find(r => r.value === reason)?.label || reason,
        notes: notes.trim(),
        status: 'MISSED'
      };

      if (isDateRange) {
        // Convert dates to Date objects
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        // Calculate the number of days between dates
        const diffTime = Math.abs(endDateObj - startDateObj);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Add 1 to include both start and end dates

        // Validate the number of fasts
        if (diffDays > 30) {
          setError('You cannot add more than 30 fasts at once');
          setIsLoading(false);
          return;
        }

        // Add the fasts
        await addBulkMissedFasts(auth.currentUser.uid, startDate, endDate, fastData);
        
        // Reset form
        setStartDate('');
        setEndDate('');
        setReason('');
        setCustomReason('');
        setNotes('');
        
        // Show success message
        toast.success(`Successfully added ${diffDays} missed ${diffDays === 1 ? 'fast' : 'fasts'}`);
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Add single fast
        await addMissedFast(auth.currentUser.uid, {
          ...fastData,
          date: new Date(startDate).toISOString(),
        });
        
        // Reset form
        setStartDate('');
        setEndDate('');
        setReason('');
        setCustomReason('');
        setNotes('');
        
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      console.error('Error adding missed fast:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center space-x-2 mb-4">
          <input
            type="checkbox"
            id="dateRange"
            checked={isDateRange}
            onChange={(e) => setIsDateRange(e.target.checked)}
            className="rounded text-[#F5B100] focus:ring-[#F5B100]"
          />
          <label htmlFor="dateRange" className="text-sm text-gray-600">
            Add multiple days
          </label>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
              {isDateRange ? 'Start Date' : 'Date Missed'}
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          </div>

          {isDateRange && (
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required={isDateRange}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
            Reason
          </label>
          <select
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            required
          >
            <option value="">Select a reason</option>
            {PREDEFINED_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.icon} {r.label}
              </option>
            ))}
          </select>
        </div>

        {reason === 'other' && (
          <div>
            <label htmlFor="customReason" className="block text-sm font-medium text-gray-700">
              Specify Reason
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PencilSquareIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="customReason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your reason"
                required
              />
            </div>
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-start mb-2">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div className="ml-3">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Add Notes
              </label>
              <p className="text-sm text-gray-500">
                Add any details about your missed {isDateRange ? 'fasts' : 'fast'} that might be helpful later
              </p>
            </div>
          </div>
          <div className="mt-2">
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
              placeholder="Examples:
• Traveled for an emergency
• Had a severe headache
• Need to take medicine"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F5B100] hover:bg-[#E5A500] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F5B100] ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isLoading ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Adding...
            </div>
          ) : (
            `Add ${isDateRange ? 'Missed Fasts' : 'Missed Fast'}`
          )}
        </button>
      </form>
    </div>
  );
}

export default AddFastForm; 